import {useEffect, useMemo} from 'react';
import {Form} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import {toast} from 'react-toastify';
import {
  CREATE_REWARD_SCHEME,
  UPDATE_REWARD_SCHEME,
} from 'graphql/mutations/rewardScheme';
import {
  GET_ALL_REWARD_SCHEMES,
  GET_REWARD_SCHEME,
} from 'graphql/queries/rewardScheme';

export const useRewardSchemeForm = (
  form,
  {rewardSchemeId, language = 'EN'} = {},
) => {
  const type = Form.useWatch('type', form);
  const rewardTrigger = Form.useWatch('rewardTrigger', form);
  const operationType = Form.useWatch('operationType', form);
  const randomStock = Form.useWatch('randomStock', form);
  const referee = Form.useWatch('referee', form);
  const referer = Form.useWatch('referer', form);
  const transactional = Form.useWatch('transactional', form);
  const segments = Form.useWatch('segments', form) || [];

  const shouldShowMinValue = useMemo(() => {
    if (type === 'REFERRAL' || type === 'LEADERBOARD') {
      return true;
    }
    return type === 'TRANSACTIONAL' && rewardTrigger === 'ANY';
  }, [type, rewardTrigger]);

  const hasRewardType = value => {
    if (referee === value || referer === value || transactional === value) {
      return true;
    }
    if (type === 'WHEEL_SPIN' && Array.isArray(segments)) {
      return segments.some(segment => segment?.reward === value);
    }
    return false;
  };

  const hasStocksReward = useMemo(
    () => hasRewardType('STOCKS'),
    [referee, referer, transactional, type, segments],
  );
  const hasCashReward = useMemo(
    () => hasRewardType('CASH_TO_INVEST'),
    [referee, referer, transactional, type, segments],
  );
  const hasFeeWaiverReward = useMemo(
    () => hasRewardType('TRANSACTION_FEE_WAIVER'),
    [referee, referer, transactional, type, segments],
  );

  const [createRewardScheme, {loading}] = useMutation(CREATE_REWARD_SCHEME);
  const [updateRewardScheme, {loading: updateLoading}] =
    useMutation(UPDATE_REWARD_SCHEME);
  const navigate = useNavigate();
  const isUpdating = Boolean(rewardSchemeId);

  const {data: rewardSchemeData, loading: queryLoading} = useQuery(
    GET_REWARD_SCHEME,
    {
      variables: {getRewardSchemeId: rewardSchemeId, language},
      skip: !isUpdating,
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
      errorPolicy: 'all',
    },
  );

  useEffect(() => {
    if (!rewardSchemeData?.getRewardScheme) {
      return;
    }

    const reward = rewardSchemeData.getRewardScheme;
    const operationConfig = reward.operationConfig || {};
    const rewardConfig = reward.rewardConfig || {};

    const mappedAllowedCurrencies =
      operationConfig.allowedCurrencies?.map(item => ({
        currency: item.currency?.currency,
        minTransactionValue: item.minTransactionValue,
      })) || [];

    const mappedMaxFeeWaivers =
      rewardConfig.transactionFeeWaiver?.maxFeeWaiverConfig?.map(item => ({
        currency: item?.currency?.currency ?? item?.currency,
        value: item?.value,
      })) || [];

    const mappedLeaderboardRewards =
      rewardConfig.rewardTiers?.map(tier => ({
        position: tier.position,
        rewardType: tier.reward?.rewardType,
        reward: {
          cash: tier.reward?.cash
            ? {
                amount: tier.reward.cash.amount,
                currency:
                  tier.reward.cash.currency?.currency ??
                  tier.reward.cash.currency,
              }
            : undefined,
          instruments: tier.reward?.instruments
            ? {
                rewardUnits: tier.reward.instruments.rewardUnits,
                instrumentId: tier.reward.instruments.instrumentId,
              }
            : undefined,
          transactionFeeWaiver: tier.reward?.transactionFeeWaiver
            ? {
                percentage: tier.reward.transactionFeeWaiver.percentage,
                maxFeeWaiverConfig:
                  tier.reward.transactionFeeWaiver.maxFeeWaiverConfig?.map(
                    cfg => ({
                      currency:
                        cfg?.currency?.currency ?? cfg?.currency ?? undefined,
                      value: cfg?.value,
                    }),
                  ) || [],
              }
            : undefined,
        },
      })) || [];

    form.setFieldsValue({
      title: reward.title,
      description: reward.description,
      type: reward.type,
      startDate: reward.startDate ? moment(reward.startDate) : null,
      expirationDate: reward.expirationDate
        ? moment(reward.expirationDate)
        : null,
      referee: reward.rewardType?.referral?.referee || 'NONE',
      referer: reward.rewardType?.referral?.referrer || 'NONE',
      transactional: reward.rewardType?.transactional || 'NONE',
      rewardTrigger: operationConfig.rewardTriggerOccurrence,
      operationType: operationConfig.type,
      assetClass: operationConfig.assetClass,
      // Returned already normalised to the class-level vocabulary, and the
      // keys as stored, so both selects seed straight from the response.
      instrumentKeys: operationConfig.instrumentKeys || [],
      cumulativeDurationUnit: operationConfig.cumulativeDurationUnit,
      cumulativeDurationValue: operationConfig.cumulativeDurationValue,
      instructions: operationConfig.instructions || [],
      allowedCurrencies: mappedAllowedCurrencies,
      // Never seeded before, so opening a scheme and saving it wiped whatever
      // caps it had — `maxFeeWaivers` read back as empty and was sent as [].
      maxFeeWaivers: mappedMaxFeeWaivers,
      segments: operationConfig.wheelSpinConfig?.segments || [],
      requireDabaPro: operationConfig.requireDabaPro,
      maxNumberOfSpins: operationConfig.wheelSpinConfig?.maxNumberOfSpins,
      expirationDurationUnit:
        operationConfig.wheelSpinConfig?.expirationDurationUnit,
      expirationDurationValue:
        operationConfig.wheelSpinConfig?.expirationDurationValue,
      units: rewardConfig.instruments?.rewardUnits,
      instrumentId: rewardConfig.instruments?.instrumentId,

      amount: rewardConfig.cash?.amount,
      disclaimer: reward.disclaimer,
      // The query returns a `WalletCurrencyObject`, but this Select's options
      // are plain codes. Seeding the object matched no option (so the field
      // rendered blank) and then posted the object back, which the gateway
      // rejects: `String cannot represent a non string value`. Every other
      // currency on this form already unwraps; this one was missed.
      cashCurrency:
        rewardConfig.cash?.currency?.currency ?? rewardConfig.cash?.currency,
      percentage: rewardConfig.transactionFeeWaiver?.percentage,
      ...(reward.maxNumberOfRewards && {
        maxReward: reward.maxNumberOfRewards,
      }),
      allowConcurrency: reward.allowConcurrency,
      minNumberOfTransactions: operationConfig.minNumberOfTransactions,
      transactionStatuses: operationConfig.transactionStatuses,
      minPortfolioInvestedAmountInXOF:
        operationConfig.minPortfolioInvestedAmountInXOF,
      rewardExpirationDurationUnit:
        operationConfig.rewardExpirationDurationUnit,
      rewardExpirationDurationValue:
        operationConfig.rewardExpirationDurationValue,
      durationDescription: operationConfig.durationDescription,
      winnersAnnouncedTimeframe: operationConfig.winnersAnnouncedTimeframe,
      prizeDistributionTimeframe: operationConfig.prizeDistributionTimeframe,
      isRecurring: operationConfig.leaderboardConfig?.isRecurring,
      rankingMethod: operationConfig.leaderboardConfig?.rankingMethod,
      tieBreaker: operationConfig.leaderboardConfig?.tieBreaker,
      leaderboardRewards: mappedLeaderboardRewards,
    });
  }, [rewardSchemeData, form]);

  const handleSave = formValues => {
    const allowedCurrencies = formValues?.allowedCurrencies || [];
    const instructions = formValues?.instructions || [];
    const maxFeeWaivers = formValues?.maxFeeWaivers || [];
    const expirationDate = formValues?.expirationDate
      ? moment(formValues.expirationDate).format('YYYY-MM-DD')
      : null;

    const sanitizedAllowedCurrencies = allowedCurrencies
      .filter(item => item?.currency)
      .map(data => ({
        currency: data?.currency,
        minTransactionValue: Number(data?.minTransactionValue || 0),
      }));

    const sanitizedInstructions = instructions
      .filter(item => item?.titleText || item?.descriptionText)
      .map(data => ({
        titleText: data?.titleText,
        descriptionText: data?.descriptionText,
      }));

    const sanitizedMaxFeeWaivers = maxFeeWaivers
      .filter(item => item?.currency)
      .map(data => ({
        currency: data?.currency,
        value: Number(data?.value || 0),
      }));

    const leaderboardRewards = formValues?.leaderboardRewards || [];
    const rewardTiers = leaderboardRewards.map((row, index) => {
      const cashAmount = row?.reward?.cash?.amount;
      const cashCurrency = row?.reward?.cash?.currency;
      // The tier form now carries `instruments`, matching the input shape.
      const stocksUnits = row?.reward?.instruments?.rewardUnits;
      const stocksInstrumentId = row?.reward?.instruments?.instrumentId;
      const feeWaiverPercentage = row?.reward?.transactionFeeWaiver?.percentage;
      const feeWaiverConfigs =
        row?.reward?.transactionFeeWaiver?.maxFeeWaiverConfig || [];
      return {
        position: index + 1,
        reward: {
          rewardType: row.rewardType,
          cash:
            cashCurrency || cashAmount
              ? {
                  amount: Number(cashAmount || 0),
                  currency: cashCurrency,
                }
              : null,
          instruments:
            stocksInstrumentId || stocksUnits
              ? {
                  rewardUnits: Number(stocksUnits || 0),
                  instrumentId: stocksInstrumentId,
                }
              : null,
          transactionFeeWaiver:
            feeWaiverPercentage || feeWaiverConfigs.length
              ? {
                  percentage: Number(feeWaiverPercentage || 0),
                  maxFeeWaiverConfig: feeWaiverConfigs.map(cfg => ({
                    currency: cfg?.currency,
                    value: Number(cfg?.value || 0),
                  })),
                }
              : null,
        },
      };
    });

    // `maxReward` only has a `Form.Item` when `shouldShowMinValue` is true, and
    // antd omits unmounted fields from the submitted values — so a scheme that
    // hides the input submitted no `maxNumberOfRewards` at all and the gateway
    // rejected the update as missing a required field, even though the stored
    // value was sitting in the form. Fall back to the store.
    // Either shape: a freshly picked option is a code, a value restored from
    // an older render could still be the whole currency object.
    const cashCurrencyCode =
      formValues?.cashCurrency?.currency ?? formValues?.cashCurrency;

    const maxRewardValue =
      formValues?.maxReward ?? form.getFieldValue('maxReward');

    const maxNumberOfRewards =
      formValues?.rewardTrigger === 'FIRST'
        ? 1
        : maxRewardValue
        ? Number(maxRewardValue)
        : undefined;

    const input = {
      allowConcurrency: Boolean(formValues?.allowConcurrency),
      description: formValues?.description,
      disclaimer: formValues?.disclaimer,
      // `startDate` is a String on the input; sending an explicit null for a
      // scheme that has no start date failed as "must be a string". Absent and
      // unset are the same thing here, so omit it instead.
      ...(formValues?.startDate && {
        startDate: moment(formValues.startDate).format('YYYY-MM-DD'),
      }),
      expirationDate,
      language,
      ...(typeof maxNumberOfRewards !== 'undefined' && {
        maxNumberOfRewards,
      }),
      operationConfig: {
        allowedCurrencies: sanitizedAllowedCurrencies,
        ...(formValues?.type === 'WHEEL_SPIN' && {
          wheelSpinConfig: {
            maxNumberOfSpins: Number(formValues?.maxNumberOfSpins || 0),
            ...(formValues?.expirationDurationUnit && {
              expirationDurationUnit: formValues.expirationDurationUnit,
            }),
            ...(formValues?.expirationDurationValue && {
              expirationDurationValue: Number(
                formValues.expirationDurationValue,
              ),
            }),
            ...(formValues?.segments && {
              segments: formValues.segments.map(reward => ({
                reward: reward?.reward,
                weight: Number(reward?.weight || 0),
              })),
            }),
          },
        }),
        assetClass: formValues?.assetClass,
        // Always sent, never omitted: the server `$set`s the whole
        // `operationConfig` subdocument, so an update that leaves this out
        // erases the scheme's instrument narrowing rather than keeping it.
        // The field is unmounted for a scheme that is not investment-scoped,
        // and antd drops unmounted fields from the submitted values, so fall
        // back to the store.
        instrumentKeys:
          formValues?.instrumentKeys ??
          form.getFieldValue('instrumentKeys') ??
          [],
        instructions: sanitizedInstructions,
        ...(formValues?.cumulativeDurationUnit && {
          cumulativeDurationUnit: formValues?.cumulativeDurationUnit,
        }),
        ...(formValues?.cumulativeDurationValue && {
          cumulativeDurationValue: Number(formValues?.cumulativeDurationValue),
        }),
        rewardTriggerOccurrence: formValues?.rewardTrigger,
        type: formValues?.operationType,
        minNumberOfTransactions: formValues?.minNumberOfTransactions ?? null,
        transactionStatuses:
          formValues?.transactionStatuses?.length > 0
            ? formValues.transactionStatuses
            : null,
        ...(formValues?.requireDabaPro !== undefined && {
          requireDabaPro: formValues?.requireDabaPro ?? false,
        }),
        minPortfolioInvestedAmountInXOF:
          formValues?.minPortfolioInvestedAmountInXOF ?? null,
        // Explicit nulls were rejected — `null` is not a member of the
        // duration-unit enum, and Mongo validates it as a value rather than as
        // "unset". A scheme with no reward expiry simply omits both.
        ...(formValues?.rewardExpirationDurationValue != null && {
          rewardExpirationDurationValue:
            formValues.rewardExpirationDurationValue,
        }),
        ...(formValues?.rewardExpirationDurationUnit != null && {
          rewardExpirationDurationUnit: formValues.rewardExpirationDurationUnit,
        }),
        // Leaderboard-only settings, gated on the type the same way
        // `wheelSpinConfig` is. They used to go out on every scheme: a plain
        // referral scheme still posted a `leaderboardConfig` and a row of
        // leaderboard nulls, which made the server build the leaderboard side
        // of the document for a scheme that has none.
        ...(formValues?.type === 'LEADERBOARD' && {
          durationDescription: formValues?.durationDescription || null,
          winnersAnnouncedTimeframe:
            formValues?.winnersAnnouncedTimeframe || null,
          prizeDistributionTimeframe:
            formValues?.prizeDistributionTimeframe || null,
          leaderboardConfig: {
            isRecurring: Boolean(formValues?.isRecurring),
            rankingMethod: formValues?.rankingMethod,
            tieBreaker: formValues?.tieBreaker,
          },
        }),
      },
      rewardConfig: {
        // Each block is sent only when the scheme actually grants that kind of
        // reward. They used to go out unconditionally, so a cash-only scheme
        // still posted an empty `instruments` and the server refused the whole
        // create with `"rewardConfig.instruments.instrumentId" is required`.
        ...(cashCurrencyCode || formValues?.amount
          ? {
              cash: {
                amount: Number(formValues?.amount || 0),
                currency: cashCurrencyCode,
              },
            }
          : {}),
        // `stocks` is still accepted but deprecated; `instruments` is the
        // key to send. What genuinely breaks is the selector inside it —
        // `RewardConfigStockInput.ticker` now returns a migration error, so
        // the instrument has to be named by `instrumentId`.
        ...(formValues?.instrumentId || formValues?.units
          ? {
              instruments: {
                rewardUnits: Number(formValues?.units || 0),
                instrumentId: formValues?.instrumentId,
              },
            }
          : {}),
        ...(formValues?.percentage || sanitizedMaxFeeWaivers.length
          ? {
              transactionFeeWaiver: {
                maxFeeWaiverConfig: sanitizedMaxFeeWaivers,
                percentage: Number(formValues?.percentage || 0),
              },
            }
          : {}),
        ...(formValues?.type === 'LEADERBOARD' && {
          rewardTiers,
        }),
      },
      rewardType: {
        referral: {
          referee: formValues?.referee,
          referrer: formValues?.referer,
        },
        transactional: formValues?.transactional,
      },
      title: formValues?.title,
      type: formValues?.type,
    };

    const mutation = isUpdating
      ? updateRewardScheme({
          variables: {
            updateRewardSchemeId: rewardSchemeId,
            input,
          },
          refetchQueries: [{query: GET_ALL_REWARD_SCHEMES}],
        })
      : createRewardScheme({
          variables: {
            input,
          },
          refetchQueries: [{query: GET_ALL_REWARD_SCHEMES}],
        });

    mutation
      .then(result => {
        const response = isUpdating
          ? result?.data?.updateRewardScheme
          : result?.data?.createRewardScheme;

        if (response?.__typename === 'Error') {
          toast.error(response.message);
        } else {
          toast.success(
            isUpdating
              ? 'Reward Scheme updated successfully'
              : 'Reward Scheme created successfully',
          );
          navigate('/dashboard/reward-schemes');
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const combinedLoading = loading || updateLoading || queryLoading;

  /**
   * The ticker of a scheme saved before instruments became the asset identity.
   *
   * Returned rather than written into the form: it is not an editable field,
   * and a value with no `Form.Item` behind it is invisible to `Form.useWatch`.
   */
  const legacyTicker = rewardSchemeData?.getRewardScheme?.rewardConfig
    ?.instruments?.instrumentId
    ? undefined
    : rewardSchemeData?.getRewardScheme?.rewardConfig?.instruments?.ticker;

  return {
    type,
    rewardTrigger,
    operationType,
    legacyTicker,
    randomStock: Boolean(randomStock),
    shouldShowMinValue,
    hasStocksReward,
    hasCashReward,
    hasFeeWaiverReward,
    handleSave,
    loading: combinedLoading,
  };
};
